import { http, HttpResponse, delay } from 'msw';

import {
  BulkAssetRequest as BulkAssetsRequestDto,
  CreateAssetsRequest as CreateAssetsRequestDto,
  PaginatedResponseAssetFileWithURL as PaginatedAssetFileWithURLDto,
} from '@electricjs/core_entity-client';

import {
  mockAssetEvents,
  mockAssetImages,
  mockAssetLogs,
  mockMac,
  mockPC,
  mockShipmentEligibleMac,
  mockShipmentEligiblePC,
  mockStorefrontAssets,
  mockUnresolvedAssetLogs,
  mockWarehouseMetrics,
} from '@/mocks/data/assetData';
import {
  employeeIdsWithDevice,
  offboardingScenarioEmployeesById,
} from '@/mocks/data/employeeData';

const mockAssetsById: Record<string, typeof mockMac> = {
  [mockMac.id]: mockMac,
  [mockPC.id]: mockPC,
};

// Host used for the fake presigned upload URLs handed out below, so the
// follow-up PUT from the image upload modal is intercepted too.
const MOCK_UPLOAD_HOST = 'https://mock-asset-uploads.electric.test';

/**
 * Returns a copy of the mock asset attributed to the requesting employee.
 * Used by the offboarding Devices step so the asset row shows the right
 * employee name without requiring a per-employee asset fixture.
 */
const assetAssignedTo = (employeeId: string) => {
  const employee = offboardingScenarioEmployeesById[employeeId];
  return {
    ...mockMac,
    assignedTo: employeeId,
    assignedToEmployee: {
      id: employeeId,
      firstName: employee?.first_name ?? '',
      lastName: employee?.last_name ?? '',
    },
  };
};

export const assetHandlers = [
  http.get('*/v1/organizations/:organizationId/assets', async ({ request }) => {
    const url = new URL(request.url);
    const assignedTo = url.searchParams.get('assigned_to');

    // The offboarding Devices step queries with `assigned_to=:employeeId` —
    // only return an asset for employees flagged as having a device. When the
    // call is un-scoped (asset inventory listings) keep returning the mock
    // asset so other pages aren't broken.
    let results: (typeof mockMac)[];
    if (assignedTo) {
      results = employeeIdsWithDevice.has(assignedTo)
        ? [assetAssignedTo(assignedTo)]
        : [];
    } else {
      results = [mockMac];
    }

    await delay(200);
    return HttpResponse.json(
      { results, total: results.length },
      { status: 200 }
    );
  }),

  http.get(
    '*/v1/organizations/:organizationId/assets/eligible-for-shipment',
    async ({ request }) => {
      const url = new URL(request.url);
      const searchTerm =
        url.searchParams.get('search_term')?.toLowerCase() ?? '';

      const eligibleAssets = [mockShipmentEligibleMac, mockShipmentEligiblePC];
      const results = searchTerm
        ? eligibleAssets.filter(asset =>
            asset.name.toLowerCase().includes(searchTerm)
          )
        : eligibleAssets;

      await delay(200);
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),

  http.get('*/v1/organizations/:organizationId/assets/storefront', async () => {
    await delay(200);
    return HttpResponse.json(
      {
        results: mockStorefrontAssets,
        total: mockStorefrontAssets.length,
        total_non_shippable: mockStorefrontAssets.filter(
          asset => !asset.is_shippable
        ).length,
      },
      { status: 200 }
    );
  }),

  http.get(
    '*/v1/organizations/:organizationId/assets/storefront/:assetId',
    async ({ params }) => {
      const asset = mockStorefrontAssets.find(
        storefrontAsset => storefrontAsset.id === params.assetId
      );
      await delay(200);
      return asset
        ? HttpResponse.json(asset, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/assets/bulk',
    async ({ request }) => {
      const { asset_ids } = (await request.json()) as BulkAssetsRequestDto;
      const assets_mapping = Object.fromEntries(
        asset_ids.flatMap(id =>
          mockAssetsById[id] ? [[id, mockAssetsById[id]]] : []
        )
      );
      await delay(200);
      return HttpResponse.json({ assets_mapping }, { status: 200 });
    }
  ),

  http.get('*/v1/organizations/:organizationId/assets/history', async () => {
    await delay(200);
    return HttpResponse.json(
      { results: mockAssetLogs, total: mockAssetLogs.length },
      { status: 200 }
    );
  }),

  http.get(
    '*/v1/organizations/:organizationId/assets/warehousing/metrics',
    async () => {
      await delay(200);
      return HttpResponse.json(mockWarehouseMetrics, { status: 200 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/assets',
    async ({ request }) => {
      const { assets } = (await request.json()) as CreateAssetsRequestDto;
      const created_assets = assets.map(asset => ({
        ...mockMac,
        id: crypto.randomUUID(),
        name: asset.name,
        serialNumber: asset.serial_number ?? '',
        assignedTo: asset.assigned_to,
        assignedToEmployee: undefined,
        device: null,
      }));
      await delay(200);
      return HttpResponse.json(
        { description: 'Assets created', created_assets },
        { status: 201 }
      );
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId',
    async ({ params }) => {
      const asset = mockAssetsById[params.assetId as string];
      await delay(200);
      return asset
        ? HttpResponse.json(asset, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.patch('*/v1/organizations/:organizationId/assets/:assetId', async () => {
    await delay(200);
    return new HttpResponse(null, { status: 204 });
  }),

  http.delete(
    '*/v1/organizations/:organizationId/assets/:assetId',
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.patch(
    '*/v1/organizations/:organizationId/assets/:assetId/location',
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId/asset_history',
    async () => {
      await delay(200);
      return HttpResponse.json(
        { results: mockAssetLogs, total: mockAssetLogs.length },
        { status: 200 }
      );
    }
  ),

  // A single page with no `last_date` cursor, so the paginated "download all"
  // loop terminates after one request.
  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId/events',
    async () => {
      await delay(200);
      return HttpResponse.json({ events: mockAssetEvents }, { status: 200 });
    }
  ),

  // `presigned-urls` must be registered before the `:assetFileId` routes.
  http.post(
    '*/v1/organizations/:organizationId/assets/:assetId/images/presigned-urls',
    async ({ request }) => {
      const formData = await request.formData();
      const presigned_urls = formData.getAll('file_names').map(name => {
        const file_id = crypto.randomUUID();
        return {
          filename: String(name),
          presigned_url: `${MOCK_UPLOAD_HOST}/${file_id}`,
          file_id,
          s3_key: `mock/${file_id}`,
          expires_in: 3600,
        };
      });
      await delay(200);
      return HttpResponse.json(
        { message: 'Presigned URLs generated', presigned_urls, errors: [] },
        { status: 200 }
      );
    }
  ),

  http.put(`${MOCK_UPLOAD_HOST}/:fileId`, async () => {
    await delay(200);
    return new HttpResponse(null, { status: 200 });
  }),

  http.post(
    '*/v1/organizations/:organizationId/assets/:assetId/images',
    async ({ request }) => {
      const formData = await request.formData();
      const uploaded_files = formData.getAll('files').map(file => ({
        filename: file instanceof File ? file.name : String(file),
        file_id: crypto.randomUUID(),
      }));
      await delay(200);
      return HttpResponse.json(
        { message: 'Files uploaded', uploaded_files, errors: [] },
        { status: 200 }
      );
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId/images',
    async () => {
      const body: PaginatedAssetFileWithURLDto = {
        results: mockAssetImages,
        total: mockAssetImages.length,
      };
      await delay(200);
      return HttpResponse.json(body, { status: 200 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId/images/:assetFileId',
    async ({ params }) => {
      const image = mockAssetImages.find(
        file => file.file_id === params.assetFileId
      );
      await delay(200);
      return image
        ? HttpResponse.json(image, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.delete(
    '*/v1/organizations/:organizationId/assets/:assetId/images/:assetFileId',
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/devices/issues/unresolved',
    async () => {
      await delay(200);
      return HttpResponse.json(mockUnresolvedAssetLogs, { status: 200 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/devices/issues/ignore/:assetLogId',
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/employees/me/assets',
    async () => {
      await delay(200);
      return HttpResponse.json(
        { results: [mockMac], total: 1 },
        { status: 200 }
      );
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/assets',
    async ({ params }) => {
      const employeeId = params.employeeId as string;
      const results = employeeIdsWithDevice.has(employeeId)
        ? [assetAssignedTo(employeeId)]
        : [];
      await delay(200);
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
];
